import {
  Input,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import {
  ALLOWED_OPERATIONAL_BILLING_STATUSES,
  BILLING_STATUSES,
  getChipDisplayClass,
  getDisplayStatus,
  INACTIVE_STATUSES,
  LOAD_STATUS,
  TRACKER_VISIBLE_STATUSES,
} from 'doc-mate-store/lib/constants/load';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';

import useStatusTransition from '../../hooks/statusTransition';
import BillingIssueModal from '../DataTable/cells/BillingIssueModal';
import InvalidStatusTransitionModal from '../DataTable/cells/InvalidStatusTransitionModal';
import OrderCancellationConfirmation from '../DataTable/cells/OrderCancellationConfirmation';
import TimestampConfirmModal from '../DataTable/cells/TimestampConfirmModal';

type Props = {
  loadId: number;
  currentStatus: LOAD_STATUS;
  displayMessage: string;
  isBillingUser: boolean;
  onStatusUpdated?: () => void;
};

export const EditableStatusSelect: React.FC<Props> = observer(
  ({
    loadId,
    currentStatus,
    displayMessage,
    isBillingUser,
    onStatusUpdated,
  }) => {
    const classes = useStyles();
    const {
      openSelect,
      setOpenSelect,
      isLoading,
      newStatus,
      openCancelModal,
      openPaperworkIssueModal,
      openInvalidModal,
      invalidMessage,
      openTimestampModal,
      isManifestWideChange,
      forwardStatusesToFill,
      reverseStatusesToClear,
      timestamps,
      setTimestamps,
      handleChange,
      executeStatusUpdate,
      handleCancel,
    } = useStatusTransition(loadId, currentStatus, onStatusUpdated);

    const handleOpenSelect = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenSelect(true);
      },
      [setOpenSelect],
    );

    const iconComponent = useCallback(
      () => (
        <span onClick={handleOpenSelect} className={classes.dropdownIcon}>
          {openSelect ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </span>
      ),
      [classes.dropdownIcon, handleOpenSelect, openSelect],
    );

    return (
      <>
        <Select
          className={classes.selectContainer}
          input={<Input disableUnderline />}
          onChange={handleChange}
          value={currentStatus}
          open={openSelect}
          onClose={() => setOpenSelect(false)}
          IconComponent={iconComponent}
          renderValue={() => (
            <Typography variant="h3" noWrap className={classes.text}>
              {displayMessage}
            </Typography>
          )}
        >
          {TRACKER_VISIBLE_STATUSES.map((stat: LOAD_STATUS) => {
            const isCurrentBilling = BILLING_STATUSES.includes(currentStatus);
            const isInactive = INACTIVE_STATUSES.includes(currentStatus);
            const isTargetBilling = BILLING_STATUSES.includes(stat);
            const isOperationalAllowed = ALLOWED_OPERATIONAL_BILLING_STATUSES.includes(
              currentStatus,
            );
            const isTargetOperationalAllowed = ALLOWED_OPERATIONAL_BILLING_STATUSES.includes(
              stat,
            );

            let isDisabled = false;

            if (
              stat === LOAD_STATUS.PENDING_ACCEPTANCE ||
              currentStatus === LOAD_STATUS.INVOICED
            ) {
              isDisabled = true;
            } else if (
              !isBillingUser &&
              (stat === LOAD_STATUS.BILLING_ISSUE ||
                stat === LOAD_STATUS.INVOICED)
            ) {
              isDisabled = true;
            } else if (isCurrentBilling) {
              if (!isTargetBilling && !isTargetOperationalAllowed)
                isDisabled = true;
            } else if (isTargetBilling) {
              if (!isOperationalAllowed) isDisabled = true;
            } else if (!isInactive && stat === LOAD_STATUS.UNASSIGNED) {
              isDisabled = true;
            }

            return (
              <MenuItem disabled={isDisabled} key={stat} value={stat}>
                <span className={getChipDisplayClass(stat)}>
                  {getDisplayStatus(stat)}
                </span>
              </MenuItem>
            );
          })}
        </Select>

        {!openSelect && openCancelModal && (
          <OrderCancellationConfirmation
            loading={isLoading}
            onConfirm={desc =>
              newStatus && executeStatusUpdate(newStatus, desc)
            }
            onCancel={handleCancel}
          />
        )}

        {!openSelect && openPaperworkIssueModal && (
          <BillingIssueModal
            loading={isLoading}
            onConfirm={desc =>
              newStatus && executeStatusUpdate(newStatus, desc)
            }
            onCancel={handleCancel}
          />
        )}

        {!openSelect && openTimestampModal && (
          <TimestampConfirmModal
            open={openTimestampModal}
            isLoading={isLoading}
            newStatus={newStatus}
            isManifestWideChange={isManifestWideChange}
            forwardStatusesToFill={forwardStatusesToFill}
            reverseStatusesToClear={reverseStatusesToClear}
            timestamps={timestamps}
            onTimestampChange={(st, val) =>
              setTimestamps((prev: any) => ({ ...prev, [st]: val }))
            }
            onConfirm={() =>
              newStatus && executeStatusUpdate(newStatus, undefined, timestamps)
            }
            onCancel={handleCancel}
          />
        )}

        {!openSelect && openInvalidModal && (
          <InvalidStatusTransitionModal
            open={openInvalidModal}
            message={invalidMessage}
            onClose={handleCancel}
          />
        )}
      </>
    );
  },
);

const useStyles = makeStyles(() => ({
  selectContainer: {
    cursor: 'pointer',
    color: 'inherit',
    width: '100%',
    '& .MuiSelect-select.MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent !important',
      paddingRight: '24px !important',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent !important',
    },
  },
  dropdownIcon: { color: 'inherit', display: 'flex', alignItems: 'center' },
  text: { textAlign: 'center', textTransform: 'uppercase', color: 'inherit' },
}));
